import React, { useState, useMemo } from "react";
import { Card, CardContent, Typography, Box, Collapse, IconButton } from "@mui/material";
import { PieChart } from "@mui/x-charts";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const PIE_COLORS = [
  '#00b8d4', '#ab47bc', '#e91e63', '#3949ab', '#2e7d32',
  '#f57c00', '#c62828', '#00695c', '#5c6bc0', '#8d6e63',
  '#ff6f00', '#1565c0', '#6a1b9a', '#43a047', '#d84315',
];

const MAX_SLICES = 8;
const OTHER_COLOR = '#9e9e9e';

const Piecharts_MUI = ({
  data,
  title = "Piechart",
  showLegend = true,
  labelPrefix = "Label",
  chartHeight = 300,
  maxWidth = 700,
  titleFontSize = 24,
  legendFontSize = 12,
  legendInformation = {},
}) => {
  const userId = data ? Object.keys(data)[0] : null;
  const rawEntriesObject = userId ? data[userId] : {};
  const rawEntries = Object.values(rawEntriesObject);
  const filteredEntries = rawEntries;

  const [otherExpanded, setOtherExpanded] = useState(false);

  const { pieData, otherItems, hasOther } = useMemo(() => {
    const all = filteredEntries.map((entry, index) => ({
      id: index,
      value: Number(entry.percent),
      label: legendInformation[index] || `${labelPrefix} ${index}`,
    }));

    const sorted = [...all].sort((a, b) => b.value - a.value);

    if (sorted.length <= MAX_SLICES) {
      return {
        pieData: sorted.map((s, i) => ({ ...s, color: PIE_COLORS[i % PIE_COLORS.length] })),
        otherItems: [],
        hasOther: false,
      };
    }

    const topItems = sorted.slice(0, MAX_SLICES).map((s, i) => ({
      ...s,
      color: PIE_COLORS[i % PIE_COLORS.length],
    }));
    const restItems = sorted.slice(MAX_SLICES);
    const otherValue = restItems.reduce((s, item) => s + item.value, 0);

    return {
      pieData: [
        ...topItems,
        { id: 'other', value: otherValue, label: `Other (${restItems.length})`, color: OTHER_COLOR },
      ],
      otherItems: restItems,
      hasOther: true,
    };
  }, [filteredEntries, legendInformation, labelPrefix]);

  if (!data) return null;

  const chartWidth = showLegend ? Math.min(Math.round(maxWidth * 0.55), 400) : maxWidth;

  return (
    <Card sx={{ maxWidth, margin: "auto", mt: 4 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom sx={{ fontSize: titleFontSize }}>
          {title}
        </Typography>

        <Box display="flex" flexWrap="wrap" gap={1} alignItems="flex-start">
          <Box flex="0 0 auto">
            <PieChart
              series={[{
                data: pieData,
                highlightScope: { fade: "global", highlight: "item" },
                faded: { innerRadius: 30, additionalRadius: -30, color: "gray" },
                valueFormatter: ({ value }) => `${value.toFixed(2)}%`,
                arcLabel: (item) => item.value > 20 ? item.label : "",
                arcLabelMinAngle: 0,
                arcLabelStyle: { fontSize: 14, fontWeight: 600, fill: "#000" },
              }]}
              width={chartWidth}
              height={chartHeight}
              slotProps={{ legend: { hidden: true } }}
            />
          </Box>

          {showLegend && (
            <Box flex="1 1 180px" sx={{ pt: 1, maxHeight: chartHeight + 40, overflowY: 'auto' }}>
              {pieData.map((item) => (
                <Box
                  key={item.id}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{
                    py: 0.4,
                    px: 0.5,
                    borderRadius: 1,
                    cursor: item.id === 'other' ? 'pointer' : 'default',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                  onClick={item.id === 'other' ? () => setOtherExpanded(prev => !prev) : undefined}
                >
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ flex: 1, fontSize: legendFontSize, minWidth: 0 }} noWrap>
                    {item.label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, fontSize: legendFontSize, whiteSpace: 'nowrap' }}>
                    {item.value.toFixed(1)}%
                  </Typography>
                  {item.id === 'other' && (
                    <IconButton size="small" sx={{ p: 0, ml: -0.5 }}>
                      {otherExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                    </IconButton>
                  )}
                </Box>
              ))}

              {hasOther && (
                <Collapse in={otherExpanded}>
                  <Box sx={{ pl: 2.5, borderLeft: '2px solid', borderColor: OTHER_COLOR, ml: 0.6, mt: 0.5 }}>
                    {otherItems.map((item) => (
                      <Box key={item.id} display="flex" alignItems="center" gap={1} sx={{ py: 0.25 }}>
                        <Typography variant="caption" sx={{ flex: 1, color: 'text.secondary', minWidth: 0, fontSize: Math.max(legendFontSize - 1, 10) }} noWrap>
                          {item.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, whiteSpace: 'nowrap', fontSize: Math.max(legendFontSize - 1, 10) }}>
                          {item.value.toFixed(1)}%
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              )}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default Piecharts_MUI;
